import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, BookOpen, Users, Award, Video, Calendar, BarChart3, FileText, Trophy, MessageCircle, Users2, Upload, Brain, CheckCircle, Target, ClipboardCheck, UserCog, Eye, Lightbulb, Receipt, CloudUpload, Settings, TrendingUp, GraduationCap } from 'lucide-react';

interface FAQCard {
  id: string;
  title: string;
  description: string;
  icon: any;
  instructions: string[];
  category: string;
}

const faqData: FAQCard[] = [
  {
    id: 'course-catalog',
    title: 'Course Catalog',
    description: 'Browse and enroll in available professional development courses',
    icon: BookOpen,
    category: 'Learning',
    instructions: [
      'Navigate to Course Catalog from the sidebar',
      'Browse courses or use filters to find specific topics',
      'Click on a course to view details and enrollment options',
      'Click "Enroll" to register for a course',
      'View your enrolled courses in "My Learning"'
    ]
  },
  {
    id: 'my-learning',
    title: 'My Learning',
    description: 'Access your enrolled courses and track your progress',
    icon: Users,
    category: 'Learning',
    instructions: [
      'Go to My Learning from the sidebar',
      'View all courses you are currently enrolled in',
      'Click on a course to continue where you left off',
      'Track completion status and earned credits',
      'Complete course modules to earn certificates'
    ]
  },
  {
    id: 'my-groups',
    title: 'My Groups',
    description: 'Join and participate in Professional Learning Communities (PLCs)',
    icon: Users2,
    category: 'Learning',
    instructions: [
      'Navigate to My Groups from the sidebar',
      'View all PLCs and groups you belong to',
      'Create new PLCs or school-based groups (admin/coordinator)',
      'Add members to your groups and manage participation',
      'Use groups for collaborative learning and meetings'
    ]
  },
  {
    id: 'portfolio',
    title: 'My Portfolio',
    description: 'Build and showcase your professional teaching portfolio',
    icon: FileText,
    category: 'Learning',
    instructions: [
      'Navigate to My Portfolio',
      'Upload lesson plans, videos, and teaching artifacts',
      'Organize materials by subject or grade level',
      'Share portfolio items with colleagues or mentors',
      'Export portfolio for NBCT certification or evaluations'
    ]
  },
  {
    id: 'video-library',
    title: 'Video Library',
    description: 'Access instructional videos and teaching demonstrations',
    icon: Video,
    category: 'Learning',
    instructions: [
      'Open Video Library from the sidebar',
      'Search for videos by topic, subject, or grade level',
      'Watch videos and take notes',
      'Save favorites for later viewing',
      'Upload your own teaching videos to share'
    ]
  },
  {
    id: 'nbct-coach',
    title: 'NBCT Coach',
    description: 'Get AI-powered coaching for National Board Certification',
    icon: Trophy,
    category: 'Learning',
    instructions: [
      'Access NBCT Coach from the sidebar',
      'Upload teaching videos for AI analysis',
      'Receive feedback based on NBCT standards',
      'Review coaching suggestions and improvements',
      'Track your progress toward certification'
    ]
  },
  {
    id: 'badges',
    title: 'Badges & Awards',
    description: 'Earn and display digital badges for completed achievements',
    icon: Award,
    category: 'Learning',
    instructions: [
      'View your earned badges in Badges & Awards',
      'Complete courses and activities to unlock new badges',
      'Share badges on social media or email',
      'See requirements for upcoming badges',
      'Track your badge collection over time'
    ]
  },
  {
    id: 'transcript',
    title: 'Transcript',
    description: 'View and download your professional development transcript',
    icon: FileText,
    category: 'Learning',
    instructions: [
      'Go to Transcript from the sidebar',
      'View all completed courses and earned credits',
      'Filter by date range or course type',
      'Download transcript as PDF for license renewal',
      'Export data for district reporting'
    ]
  },
  {
    id: 'course-proposals',
    title: 'Course Proposals',
    description: 'Submit proposals for new professional development courses',
    icon: FileText,
    category: 'Course Management',
    instructions: [
      'Navigate to Course Proposals',
      'Click "Create New Proposal"',
      'Fill in course details, objectives, and target audience',
      'Attach supporting materials if needed',
      'Submit for admin review and approval'
    ]
  },
  {
    id: 'course-builder',
    title: 'Course Builder',
    description: 'Create and manage professional development courses',
    icon: BookOpen,
    category: 'Course Management',
    instructions: [
      'Open Course Builder from the sidebar',
      'Click "Create New Course"',
      'Add modules, lessons, and content',
      'Upload videos, documents, and assessments',
      'Publish course when ready for enrollment'
    ]
  },
  {
    id: 'admin-dashboard',
    title: 'Admin Dashboard',
    description: 'View district-wide analytics and manage users',
    icon: BarChart3,
    category: 'Administration',
    instructions: [
      'Access Admin Dashboard (admin users only)',
      'View enrollment statistics and completion rates',
      'Monitor PD hours and compliance',
      'Track district-wide KPIs',
      'Export reports for leadership'
    ]
  },
  {
    id: 'manage-users',
    title: 'Manage Users',
    description: 'Add, edit, and manage user accounts in your district',
    icon: Users,
    category: 'Administration',
    instructions: [
      'Go to Manage Users (admin only)',
      'Click "Add User" to create new accounts',
      'Edit user profiles and roles',
      'Bulk import users via CSV file',
      'Reset passwords and manage permissions'
    ]
  },
  {
    id: 'meeting-tracker',
    title: 'Meeting Tracker',
    description: 'Schedule and track mentor/mentee meetings',
    icon: Calendar,
    category: 'Mentoring',
    instructions: [
      'Open Meeting Tracker from the sidebar',
      'Schedule new meetings with mentees',
      'Record meeting notes and KPIs',
      'Track meeting attendance and duration',
      'Generate meeting reports for compliance'
    ]
  },
  {
    id: 'chat-support',
    title: 'Chat Support',
    description: 'Get help through live chat or submit support requests',
    icon: MessageCircle,
    category: 'Support',
    instructions: [
      'Click "Support & Help" in the main navigation',
      'Live chat is available 9am-5pm EST',
      'Outside hours, submit an offline request',
      'Search FAQ for quick answers',
      'Admins can view chat history in Admin Chat'
    ]
  },
  {
    id: 'practice-videos',
    title: 'Practice Video Upload',
    description: 'Upload teaching videos and receive AI-powered coaching feedback',
    icon: Video,
    category: 'Learning',
    instructions: [
      'Navigate to "Putting it Into Practice"',
      'Upload a video of your teaching practice',
      'AI analyzes your video for instructional techniques',
      'Receive detailed coaching feedback with timestamps',
      'Review research-based suggestions for improvement'
    ]
  },
  {
    id: 'video-assignments',
    title: 'Video Assignments',
    description: 'Assign coaching videos to teachers for professional growth',
    icon: Video,
    category: 'Mentoring',
    instructions: [
      'Go to Video Assignments (mentors/admins)',
      'Select a teacher to assign a video',
      'Browse the video library and choose content',
      'Add notes and reflection questions',
      'Track completion and review reflections'
    ]
  },
  {
    id: 'personal-insights',
    title: 'Personal Insights',
    description: 'View personalized analytics about your professional development',
    icon: Lightbulb,
    category: 'Learning',
    instructions: [
      'Access Personal Insights from your dashboard',
      'View your learning patterns and trends',
      'See personalized recommendations',
      'Track your progress over time',
      'Identify areas for growth'
    ]
  },
  {
    id: 'calendar-integrations',
    title: 'Calendar Integrations',
    description: 'Sync your meetings and events with external calendars',
    icon: Calendar,
    category: 'Learning',
    instructions: [
      'Go to Integrations from the sidebar',
      'Connect Google Calendar or Outlook (coming soon)',
      'Download iCal file for all upcoming events',
      'Events automatically sync to your calendar',
      'Stay organized with all PD activities'
    ]
  },
  {
    id: 'admin-intelligence',
    title: 'Admin Intelligence',
    description: 'AI-powered insights and analytics for district administrators',
    icon: Brain,
    category: 'Administration',
    instructions: [
      'Access Admin Intelligence (admin only)',
      'View AI-generated insights about district trends',
      'Get recommendations for PD improvements',
      'Analyze engagement and completion patterns',
      'Export intelligence reports for leadership'
    ]
  },
  {
    id: 'mentor-intelligence',
    title: 'Mentor Intelligence',
    description: 'AI-powered insights for mentor program coordinators',
    icon: Brain,
    category: 'Mentoring',
    instructions: [
      'Open Mentor Intelligence (coordinators/mentors)',
      'View mentee progress and engagement',
      'Get AI recommendations for mentor support',
      'Identify at-risk new teachers early',
      'Track program effectiveness metrics'
    ]
  },
  {
    id: 'mentoring-reports',
    title: 'Mentoring Reports',
    description: 'Track mentoring minutes and generate compliance reports',
    icon: BarChart3,
    category: 'Mentoring',
    instructions: [
      'Navigate to Mentoring Reports',
      'View total mentoring, coaching, and observation minutes',
      'Filter by date range or mentor',
      'See trends and meeting frequency',
      'Export reports for state compliance'
    ]
  },
  {
    id: 'attendance-manager',
    title: 'Attendance Manager',
    description: 'Track and manage course attendance for live sessions',
    icon: CheckCircle,
    category: 'Course Management',
    instructions: [
      'Open Attendance Manager (instructors/admins)',
      'Select a course and session date',
      'Mark participants as present, absent, or late',
      'Add notes about attendance issues',
      'Generate attendance reports'
    ]
  },
  {
    id: 'completion-manager',
    title: 'Completion Manager',
    description: 'Manage and verify course completion status',
    icon: GraduationCap,
    category: 'Course Management',
    instructions: [
      'Go to Completion Manager (instructors/admins)',
      'View all enrolled participants',
      'Mark courses as complete or incomplete',
      'Review completion criteria and requirements',
      'Issue certificates upon completion'
    ]
  },
  {
    id: 'certificate-management',
    title: 'Certificate Management',
    description: 'Create, design, and issue professional development certificates',
    icon: Award,
    category: 'Administration',
    instructions: [
      'Access Certificate Management (admin only)',
      'Create custom certificate templates',
      'Design certificates with your district branding',
      'Assign certificates to courses',
      'View issued certificates and download records'
    ]
  },
  {
    id: 'survey-management',
    title: 'Survey Management',
    description: 'Create and manage course evaluations and feedback surveys',
    icon: ClipboardCheck,
    category: 'Administration',
    instructions: [
      'Navigate to Survey Management (admin/coordinator)',
      'Create new surveys with custom questions',
      'Use templates for course evaluations',
      'Map survey responses to district KPIs',
      'View response data and analytics'
    ]
  },
  {
    id: 'kpi-management',
    title: 'KPI Management',
    description: 'Define and track key performance indicators',
    icon: Target,
    category: 'Administration',
    instructions: [
      'Go to KPI Management (admin only)',
      'Create district-specific KPIs',
      'Set target values and measurement frequency',
      'Track KPI values over time in meetings',
      'Generate KPI reports and trend analysis'
    ]
  },
  {
    id: 'purchase-orders',
    title: 'Purchase Order Management',
    description: 'Manage purchase orders and track district spending',
    icon: Receipt,
    category: 'Administration',
    instructions: [
      'Access Purchase Orders (admin only)',
      'Upload PO documents and extract data',
      'Track PO status and spending',
      'Manage invoice creation and billing',
      'Export financial reports'
    ]
  },
  {
    id: 'public-po-upload',
    title: 'Public PO Submissions',
    description: 'Public form for districts to submit purchase orders',
    icon: CloudUpload,
    category: 'Administration',
    instructions: [
      'Share public PO upload link with districts',
      'Districts upload POs without login',
      'AI extracts key information automatically',
      'Admins review and convert to internal POs',
      'Track submission status and follow-up'
    ]
  },
  {
    id: 'integrations-guide',
    title: 'District Integrations',
    description: 'Connect SIS, Active Directory, and other district systems',
    icon: Settings,
    category: 'Administration',
    instructions: [
      'Open Integrations Guide (admin only)',
      'Follow step-by-step setup instructions',
      'Configure Tyler Tech, PowerSchool, or custom SIS',
      'Set up Active Directory sync for user management',
      'Test integrations and enable auto-sync'
    ]
  },
  {
    id: 'quarterly-learning',
    title: 'Quarterly Learning Days',
    description: 'Plan and manage district-wide professional development days',
    icon: Calendar,
    category: 'Administration',
    instructions: [
      'Navigate to Quarterly Learning Days (admin/coordinator)',
      'Create new learning day events',
      'Schedule multiple concurrent sessions',
      'Assign instructors and locations',
      'Track registration and attendance'
    ]
  },
  {
    id: 'rfp-guide',
    title: 'RFP Evaluation Guide',
    description: 'Resources for evaluating and responding to RFPs',
    icon: FileText,
    category: 'Administration',
    instructions: [
      'Access RFP Guide (admin only)',
      'Review platform capabilities and features',
      'Access pre-written responses to common questions',
      'Download technical specifications',
      'Generate custom proposals for districts'
    ]
  },
  {
    id: 'user-provisioning',
    title: 'Trial User Provisioning',
    description: 'Manage trial accounts and user provisioning',
    icon: UserCog,
    category: 'Administration',
    instructions: [
      'Go to Trial Requests (admin only)',
      'Review incoming trial user requests',
      'Approve or deny trial accounts',
      'Set trial expiration dates',
      'Convert trials to full accounts'
    ]
  },
  {
    id: 'user-impersonation',
    title: 'User Impersonation',
    description: 'View the platform as another user for support and testing',
    icon: Eye,
    category: 'Administration',
    instructions: [
      'Access Impersonation (admin only)',
      'Search for a user by name or email',
      'Click to impersonate and view their experience',
      'Test features and troubleshoot issues',
      'Exit impersonation when finished'
    ]
  },
  {
    id: 'district-analytics',
    title: 'District Usage Analytics',
    description: 'Comprehensive analytics about platform usage and engagement',
    icon: TrendingUp,
    category: 'Administration',
    instructions: [
      'Open District Analytics (admin only)',
      'View usage trends over time',
      'Analyze feature adoption rates',
      'Track user engagement metrics',
      'Export detailed usage reports'
    ]
  }
];

export default function FAQ() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = faqData.filter(faq => 
    faq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.instructions.some(inst => inst.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const categories = Array.from(new Set(faqData.map(faq => faq.category)));

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Help Center & Feature Guide</h1>
        <p className="text-muted-foreground">
          Search for features and learn how to use the platform
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder="Search features, instructions, or categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {categories.map(category => {
        const categoryFAQs = filteredFAQs.filter(faq => faq.category === category);
        
        if (categoryFAQs.length === 0) return null;

        return (
          <div key={category} className="space-y-4">
            <h2 className="text-2xl font-semibold">{category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryFAQs.map(faq => (
                <Card key={faq.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <faq.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{faq.title}</CardTitle>
                        <CardDescription>{faq.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-muted-foreground">How to use:</p>
                      <ol className="text-sm space-y-1 list-decimal list-inside">
                        {faq.instructions.map((instruction, idx) => (
                          <li key={idx} className="text-muted-foreground">
                            {instruction}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {filteredFAQs.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No results found. Try a different search term.</p>
        </div>
      )}
    </div>
  );
}
